import React from 'react';
import { contactInfoItems, interestOptions, contactContent } from '../data';
import { ContactProps, ContactFormData } from '../types';
import ContactPresentation from './ContactPresentation';

// Contact API endpoint - default to localhost for development
const CONTACT_API_URL = import.meta.env.VITE_CONTACT_API_URL || 'http://localhost:8080';

const ContactContainer: React.FC<ContactProps> = ({ 
  className, 
  id = 'contact' 
}) => {
  const handleSubmit = async (formData: ContactFormData): Promise<void> => {
    const submissionId = Math.random().toString(36).substring(2, 15);
    const startTime = new Date();
    
    try {
      console.log(`[${startTime.toISOString()}][${submissionId}] Starting contact form submission:`, {
        apiUrl: CONTACT_API_URL,
        email: formData.email,
        subject: formData.subject
      });
      
      // Send form data to the Contact API with a longer timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds timeout
      
      console.log(`[${new Date().toISOString()}][${submissionId}] Sending request to API`);
      const response = await fetch(`${CONTACT_API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Submission-ID': submissionId
        },
        body: JSON.stringify(formData),
        signal: controller.signal,
        // Use mode: 'cors' for cross-origin requests
        mode: 'cors',
        // Don't include credentials as they may cause preflight issues
        credentials: 'omit'
      });
      
      clearTimeout(timeoutId);
      
      // If the response isn't ok, try to get more details
      if (!response.ok) {
        console.log(`[${new Date().toISOString()}][${submissionId}] Received error response:`, {
          status: response.status,
          statusText: response.statusText
        });
        
        let errorMessage = `Failed to submit form: ${response.status} ${response.statusText}`;
        let errorDetails = null;
        
        try {
          // Try to parse the error response
          const errorData = await response.json();
          console.log(`[${new Date().toISOString()}][${submissionId}] Error data:`, errorData);
          
          errorMessage = errorData.error || errorMessage;
          errorDetails = errorData.details || null;
          
          if (errorDetails) {
            errorMessage += ` (${errorDetails})`;
          }
        } catch (parseError) {
          // If parsing fails, use the status text
          console.error(`[${new Date().toISOString()}][${submissionId}] Failed to parse error response:`, parseError);
        }
        
        // Log detailed error information
        console.error(`[${new Date().toISOString()}][${submissionId}] Contact API error details:`, {
          url: `${CONTACT_API_URL}/api/contact`,
          status: response.status,
          statusText: response.statusText,
          errorMessage
        });
        
        throw new Error(errorMessage);
      }
      
      // Parse successful response
      let responseData;
      try {
        responseData = await response.json();
        console.log(`[${new Date().toISOString()}][${submissionId}] Response data:`, responseData);
      } catch (parseError) {
        console.log(`[${new Date().toISOString()}][${submissionId}] No JSON response body`);
      }
      
      // Calculate total time
      const endTime = new Date();
      const duration = endTime.getTime() - startTime.getTime();
      
      // Log successful submission
      console.log(`[${endTime.toISOString()}][${submissionId}] Form submitted successfully in ${duration}ms`);
    } catch (error) {
      // Check if this is an abort error (timeout)
      if (error instanceof DOMException && error.name === 'AbortError') {
        const errorMessage = `Submission timed out after 30 seconds. Please try again or contact us directly.`;
        console.error(`[${new Date().toISOString()}][${submissionId}] Submission timed out:`, {
          apiUrl: CONTACT_API_URL,
          email: formData.email
        });
        throw new Error(errorMessage);
      }
      
      // Log detailed error information including API URL
      console.error(`[${new Date().toISOString()}][${submissionId}] Error submitting form:`, {
        error,
        apiUrl: CONTACT_API_URL,
        message: error instanceof Error ? error.message : 'Unknown error'
      });
      
      throw error; // Re-throw the error to be handled by the presentation component
    }
  };

  return (
    <ContactPresentation
      id={id}
      heading={contactContent.heading}
      description={contactContent.description}
      contactInfoItems={contactInfoItems}
      interestOptions={interestOptions}
      onSubmit={handleSubmit}
    />
  );
};

export default ContactContainer;